import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CityMultiSelect } from "../../components/CityMultiSelect";
import { EducationLevelPills } from "../../components/EducationLevelPills";
import {
  FlowPreviewStepper,
  FlowTemplatePicker,
  type FlowSelection,
  type FlowSelectionSource,
} from "../../components/FlowTemplatePicker";
import { HiringFlowBuilder } from "../../components/HiringFlowBuilder";
import { RequiredSkillsEditor } from "../../components/RequiredSkillsEditor";
import { ResumeParseSection } from "../../components/ResumeParseSection";
import { renderSegmentOption } from "../../components/SegmentOptionLabel";
import { AiFilledField, ContentCard, PageHeader } from "../../components/ui";
import { companyApi, contractsApi, hiringFlowsApi, jobsApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { useRoleFamilyCatalog } from "../../lib/useRoleFamilyCatalog";
import { ROLE_FAMILY_LABEL, ROLE_FAMILY_PLACEHOLDER } from "../../constants/matching";
import { HR_KYC_COPY, hrCanPublish, ORG_KYC_COPY } from "../../lib/companyKyc";
import { educationLevelLabel } from "../../lib/educationCatalog";
import { catalogFuzzyFilter } from "../../lib/catalogFuzzyFilter";
import { extractDocumentText, validateDocumentFile } from "../../lib/extractDocumentText";
import { DEFAULT_PRESET_ID, getPresetStages } from "../../lib/flowPresets";
import { formatCities, formatPaise, SEGMENT_LABELS, STAGE_TYPE_LABELS } from "../../lib/format";
import {
  validateJobCities,
  validateJobCtcRange,
  validateJobRateCard,
  validateJobTitle,
  validateWalkinAddress,
} from "../../lib/jobForm";
import {
  CONTACT_PREFERENCE_LABELS,
  CTC_PERIOD_AMOUNT_LABEL,
  CTC_PERIOD_SWITCH_LABEL,
  ENGLISH_LEVEL_REQUIRED_OPTIONS,
  EXPERIENCE_TYPE_LABELS,
  EXPERIENCE_TYPE_OPTIONS,
  keysWithValues,
  PAY_TYPE_LABELS,
  PAY_TYPE_OPTIONS,
  SEGMENT_SKILL_SUGGESTIONS,
  WHATSAPP_ALERT_LABELS,
  WORK_MODE_LABELS,
  WORK_MODE_OPTIONS,
  workModeFromJob,
  type ContactPreference,
  type CtcPeriod,
  type EnglishLevelRequired,
  type ExperienceType,
  type PayType,
  type WhatsappAlert,
  type WorkMode,
} from "../../lib/formCatalogs";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { perkLabels, PERK_CATALOG } from "../../lib/perksCatalog";
import { axioTokens } from "../../theme";
import { useSegmentCatalog } from "../../lib/useSegmentCatalog";
import type { RequiredSkill, Segment, StageInput } from "../../types/api";

const IMPORT_STEP = 0;
const DETAILS_STEP = 1;
const FLOW_STEP = 2;
const REVIEW_STEP = 3;
const EDITABLE_JOB_STATUSES = new Set(["draft", "paused"]);
const LIVE_JOB_EDIT_LOCKED_MESSAGE = "This job is already live and cannot be edited.";
const JOB_DETAILS_COLUMNS = { base: 1, sm: 2 } as const;
const JOB_PAY_TYPE_COLUMNS = { base: 1, [axioTokens.containerQuery.lg]: 3 } as const;
const JOB_SECTION_GAP = "var(--axio-unit-40)";
const JOB_FIELD_GAP = "var(--axio-unit-24)";
const JOB_HEADING_GAP = "var(--axio-unit-6)";
const CTC_PERIOD_UNIT_WIDTH = "var(--axio-unit-100)";

type CatalogOption = { label: string; value: string };

type JobDetailsSectionProps = {
  title: string;
  description: string;
  children: ReactNode;
};

function JobDetailsSection({ title, description, children }: JobDetailsSectionProps) {
  return (
    <ContentCard padding="xl">
      <Stack gap={JOB_FIELD_GAP}>
        <Stack gap={JOB_HEADING_GAP}>
          <Title order={3} size="h4">{title}</Title>
          <Text size="sm" c="dimmed">{description}</Text>
        </Stack>
        {children}
      </Stack>
    </ContentCard>
  );
}

function OptionRadioGroup({
  label,
  description,
  options,
  ...groupProps
}: {
  label: ReactNode;
  description?: ReactNode;
  options: readonly CatalogOption[];
} & Omit<ComponentProps<typeof Radio.Group>, "children">) {
  return (
    <Radio.Group label={label} description={description} {...groupProps}>
      <Group mt="xs" gap="lg" wrap="wrap">
        {options.map((option) => (
          <Radio key={option.value || option.label} value={option.value} label={option.label} />
        ))}
      </Group>
    </Radio.Group>
  );
}

function PayTypeRadioCards({
  value,
  onChange,
}: {
  value: PayType;
  onChange: (next: string) => void;
}) {
  return (
    <Radio.Group label="Pay type" value={value} onChange={onChange}>
      <SimpleGrid type="container" cols={JOB_PAY_TYPE_COLUMNS} spacing={JOB_FIELD_GAP} mt="xs">
        {PAY_TYPE_OPTIONS.map((option) => {
          const selected = value === option.value;
          return (
            <Radio.Card
              key={option.value}
              value={option.value}
              withBorder
              p="md"
              radius={axioTokens.radius.md}
              style={{
                borderRadius: "var(--axio-radius)",
                borderColor: selected ? axioTokens.violet : axioTokens.border,
                backgroundColor: selected ? axioTokens.violetLight : axioTokens.surface,
                boxShadow: axioTokens.shadow.none,
              }}
            >
              <Group wrap="nowrap" align="flex-start" gap="sm">
                <Radio.Indicator />
                <Stack gap={JOB_HEADING_GAP}>
                  <Text size="sm" fw="var(--axio-font-weight-medium)">
                    {option.label}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {option.description}
                  </Text>
                </Stack>
              </Group>
            </Radio.Card>
          );
        })}
      </SimpleGrid>
    </Radio.Group>
  );
}

function stagesEqual(a: StageInput[], b: StageInput[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (s, i) =>
      s.name === b[i].name &&
      s.stage_type === b[i].stage_type &&
      s.is_mandatory === b[i].is_mandatory,
  );
}

function ctcToYearlyRupees(amount: number | undefined, period: CtcPeriod): number | undefined {
  if (amount == null) return undefined;
  return period === "monthly" ? amount * 12 : amount;
}

function convertCtcForPeriod(
  amount: number | undefined,
  from: CtcPeriod,
  to: CtcPeriod,
): number | undefined {
  if (amount == null || from === to) return amount;
  if (from === "monthly" && to === "yearly") return Math.round(amount * 12);
  return Math.round(amount / 12);
}

function otherCtcPeriod(period: CtcPeriod): CtcPeriod {
  return period === "monthly" ? "yearly" : "monthly";
}

function CtcPeriodUnitAnchor({
  period,
  onSwitch,
}: {
  period: CtcPeriod;
  onSwitch: () => void;
}) {
  const nextPeriod = otherCtcPeriod(period);
  return (
    <Anchor
      href="#"
      size="sm"
      underline="hover"
      aria-label={CTC_PERIOD_SWITCH_LABEL[nextPeriod]}
      onClick={(event) => {
        event.preventDefault();
        onSwitch();
      }}
    >
      {CTC_PERIOD_AMOUNT_LABEL[period]}
    </Anchor>
  );
}

function rupeesLabel(amount: number | undefined): string {
  return `₹${(amount ?? 0).toLocaleString("en-IN")}`;
}

export function JobWizardPage() {
  const { flatOptions: segmentOptions } = useSegmentCatalog();
  const { options: roleFamilyOptions } = useRoleFamilyCatalog();
  const { jobId } = useParams<{ jobId: string }>();
  const isEdit = !!jobId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateIdParam = searchParams.get("templateId");
  const queryClient = useQueryClient();
  const [active, setActive] = useState(isEdit ? DETAILS_STEP : IMPORT_STEP);
  const [stages, setStages] = useState<StageInput[]>(() => getPresetStages(DEFAULT_PRESET_ID));
  const [selectedId, setSelectedId] = useState<string | null>(DEFAULT_PRESET_ID);
  const [selectedSource, setSelectedSource] = useState<FlowSelectionSource | null>("preset");
  const [baselineStages, setBaselineStages] = useState<StageInput[]>(() => getPresetStages(DEFAULT_PRESET_ID));
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [ctcPeriod, setCtcPeriod] = useState<CtcPeriod>("monthly");
  const [jdFile, setJdFile] = useState<File | null>(null);
  const [jdBusy, setJdBusy] = useState(false);
  const [jdError, setJdError] = useState<string | null>(null);
  const [jdHint, setJdHint] = useState<string | null>(null);
  const [parsedFields, setParsedFields] = useState<Set<string>>(new Set());
  const templateLoadedRef = useRef(false);
  const saveInFlightRef = useRef(false);

  const companyQuery = useQuery({ queryKey: ["my-company"], queryFn: companyApi.myCompany });
  const templatesQuery = useQuery({
    queryKey: ["hiring-templates", companyQuery.data?.company_id],
    queryFn: () => hiringFlowsApi.listTemplates(companyQuery.data!.company_id),
    enabled: !!companyQuery.data?.company_id,
  });

  const jobQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobsApi.get(jobId!),
    enabled: isEdit,
  });
  const contractQuery = useQuery({
    queryKey: ["hr-contracts"],
    queryFn: contractsApi.listHrContracts,
    retry: false,
  });

  const form = useForm({
    initialValues: {
      title: "",
      description: "",
      segment: "bpo_ops" as Segment,
      cities: [] as string[],
      work_mode: "wfo" as WorkMode,
      is_walkin: false,
      walkin_address: "",
      openings_total: 1,
      pay_type: "fixed" as PayType,
      ctc_min: undefined as number | undefined,
      ctc_max: undefined as number | undefined,
      incentive_min: undefined as number | undefined,
      incentive_max: undefined as number | undefined,
      perk_ids: [] as string[],
      company_contract_id: "" as string,
      fee_basis: "flat" as "flat" | "pct_ctc",
      fee_flat: undefined as number | undefined,
      fee_pct: undefined as number | undefined,
      guarantee_days: 30,
      requires_joining_fee: false,
      requirements: "",
      required_skills: [] as RequiredSkill[],
      experience_type: "any" as ExperienceType,
      experience_min: undefined as number | undefined,
      experience_max: undefined as number | undefined,
      education_level: null as string | null,
      english_level_required: null as EnglishLevelRequired | null,
      priority: "normal",
      required_languages: [] as string[],
      max_notice_days: undefined as number | undefined,
      contact_preference: "recruiter_contacts_first" as ContactPreference,
      whatsapp_alert: "per_application_self" as WhatsappAlert,
      role_family_code: null as string | null,
    },
    validate: {
      title: validateJobTitle,
      cities: validateJobCities,
      company_contract_id: (v) => validateJobRateCard(v, Boolean(contractQuery.data?.length)),
      walkin_address: (v, values) => validateWalkinAddress(v, values.is_walkin),
      ctc_min: (v, values) => validateJobCtcRange(v, values.ctc_max),
      ctc_max: (v, values) => validateJobCtcRange(values.ctc_min, v),
    },
  });

  useEffect(() => {
    if (jobQuery.data) {
      const j = jobQuery.data;
      form.setValues({
        title: j.title,
        description: j.description ?? "",
        segment: j.segment,
        cities: [...(j.cities ?? [])],
        work_mode: workModeFromJob(j.work_mode, j.is_remote),
        is_walkin: j.is_walkin ?? false,
        walkin_address: j.walkin_address ?? "",
        openings_total: j.openings_total,
        pay_type: (j.pay_type as PayType) || "fixed",
        ctc_min: j.ctc_min_paise != null ? j.ctc_min_paise / 100 : undefined,
        ctc_max: j.ctc_max_paise != null ? j.ctc_max_paise / 100 : undefined,
        incentive_min: j.incentive_min_paise != null ? Math.round(j.incentive_min_paise / 100) : undefined,
        incentive_max: j.incentive_max_paise != null ? Math.round(j.incentive_max_paise / 100) : undefined,
        perk_ids: [...(j.perk_ids ?? [])],
        fee_basis: j.fee_basis,
        fee_flat: j.fee_flat_paise != null ? j.fee_flat_paise / 100 : undefined,
        fee_pct: j.fee_pct_ctc ?? undefined,
        guarantee_days: j.guarantee_days,
        company_contract_id: j.company_contract_id ?? "",
        requires_joining_fee: j.requires_joining_fee ?? false,
        requirements: j.requirements ?? "",
        required_skills: (j.required_skills ?? []).map((s) => ({
          skill: s.skill,
          must_have: !!s.must_have,
        })),
        experience_type: (j.experience_type as ExperienceType) || "any",
        experience_min: j.experience_min ?? undefined,
        experience_max: j.experience_max ?? undefined,
        education_level: j.education_level ?? null,
        english_level_required: j.english_level_required ?? null,
        priority: j.priority || "normal",
        required_languages: [...(j.required_languages ?? [])],
        max_notice_days: j.max_notice_days ?? undefined,
        contact_preference: (j.contact_preference as ContactPreference) || "recruiter_contacts_first",
        whatsapp_alert: (j.whatsapp_alert as WhatsappAlert) || "per_application_self",
        role_family_code: j.role_family_code ?? null,
      });
      setCtcPeriod("yearly");
      if (j.stages.length > 0) {
        const mapped = j.stages.map((s) => ({
          id: s.id,
          name: s.name,
          stage_type: s.stage_type,
          is_mandatory: s.is_mandatory,
          description: s.description,
        }));
        setStages(mapped);
        setBaselineStages(mapped);
        setSelectedId(null);
        setSelectedSource(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuery.data]);

  const selectedContract =
    (contractQuery.data ?? []).find((c) => c.id === form.values.company_contract_id) ?? null;

  function applyContractSelection(contractId: string | null) {
    const selected = (contractQuery.data ?? []).find((c) => c.id === contractId);
    form.setFieldValue("company_contract_id", contractId ?? "");
    if (!selected) return;
    form.setValues({
      company_contract_id: selected.id,
      fee_basis: selected.fee_basis,
      fee_flat: selected.fee_flat_paise != null ? selected.fee_flat_paise / 100 : undefined,
      fee_pct: selected.fee_pct_ctc ?? undefined,
      guarantee_days: selected.guarantee_days,
    });
  }

  useEffect(() => {
    if (isEdit || !templateIdParam || templateLoadedRef.current || !templatesQuery.data) return;
    const template = templatesQuery.data.find((t) => t.id === templateIdParam);
    if (template) {
      const mapped = template.stages.map((s) => ({
        name: s.name,
        stage_type: s.stage_type,
        is_mandatory: s.is_mandatory,
        description: s.description,
      }));
      setStages(mapped);
      setBaselineStages(mapped);
      setSelectedId(template.id);
      setSelectedSource("company");
      templateLoadedRef.current = true;
    }
  }, [isEdit, templateIdParam, templatesQuery.data]);

  function handleFlowSelect(selection: FlowSelection) {
    setSelectedId(selection.id);
    setSelectedSource(selection.source);
    setStages(selection.stages.map((s) => ({ ...s })));
    setBaselineStages(selection.stages.map((s) => ({ ...s })));
  }

  function handleStagesChange(next: StageInput[]) {
    setStages(next);
  }

  const hasEdits = !stagesEqual(stages, baselineStages);
  const showAiGaps = parsedFields.size > 0;
  function filled(key: string) {
    return parsedFields.has(key);
  }

  function buildJobPayload() {
    const v = form.values;
    const existing = jobQuery.data;
    const nextContractId = v.company_contract_id || undefined;
    const includeContractId =
      !isEdit ||
      !existing ||
      existing.status === "draft" ||
      nextContractId !== (existing.company_contract_id ?? undefined);
    const fresher = v.experience_type === "fresher";
    const showFixed = v.pay_type !== "incentive_only";
    const showIncentive = v.pay_type !== "fixed";

    return {
      title: v.title,
      description: v.description || undefined,
      segment: v.segment,
      cities: v.cities,
      work_mode: v.work_mode,
      is_remote: v.work_mode === "wfh",
      is_walkin: v.is_walkin,
      walkin_address: v.is_walkin ? v.walkin_address.trim() || undefined : undefined,
      openings_total: v.openings_total,
      pay_type: v.pay_type,
      ctc_min_paise: showFixed
        ? (() => {
            const yearly = ctcToYearlyRupees(v.ctc_min, ctcPeriod);
            return yearly != null ? Math.round(yearly * 100) : undefined;
          })()
        : undefined,
      ctc_max_paise: showFixed
        ? (() => {
            const yearly = ctcToYearlyRupees(v.ctc_max, ctcPeriod);
            return yearly != null ? Math.round(yearly * 100) : undefined;
          })()
        : undefined,
      incentive_min_paise: showIncentive && v.incentive_min != null ? Math.round(v.incentive_min * 100) : undefined,
      incentive_max_paise: showIncentive && v.incentive_max != null ? Math.round(v.incentive_max * 100) : undefined,
      perk_ids: v.perk_ids,
      ...(includeContractId ? { company_contract_id: nextContractId } : {}),
      requires_joining_fee: v.requires_joining_fee,
      requirements: v.requirements || undefined,
      required_skills: v.required_skills,
      experience_type: v.experience_type,
      experience_min: fresher ? 0 : v.experience_min,
      experience_max: fresher ? 1 : v.experience_max,
      education_level: v.education_level || undefined,
      english_level_required: v.english_level_required || undefined,
      priority: v.priority,
      required_languages: v.required_languages,
      max_notice_days: v.max_notice_days,
      contact_preference: v.contact_preference,
      whatsapp_alert: v.whatsapp_alert,
      role_family_code: v.role_family_code || undefined,
    };
  }

  async function handleJdImport(file: File | null) {
    setJdFile(file);
    setJdError(null);
    setJdHint(null);
    if (!file) return;
    const validationError = validateDocumentFile(file, { label: "Job description" });
    if (validationError) {
      setJdError(validationError);
      setJdFile(null);
      return;
    }
    setJdBusy(true);
    try {
      const text = await extractDocumentText(file, { label: "job description" });
      const parsed = await jobsApi.parseDocument({ text });
      const updates: Record<string, unknown> = {};

      if (parsed.title) updates.title = parsed.title;
      if (parsed.segment) updates.segment = parsed.segment as Segment;
      if (parsed.role_family_code) updates.role_family_code = parsed.role_family_code;
      if (parsed.cities?.length) updates.cities = parsed.cities;
      if (parsed.work_mode === "wfo" || parsed.work_mode === "wfh" || parsed.work_mode === "field") {
        updates.work_mode = parsed.work_mode;
      } else if (parsed.is_remote != null) {
        updates.work_mode = parsed.is_remote ? "wfh" : "wfo";
      }
      if (parsed.openings_total != null) updates.openings_total = parsed.openings_total;
      if (parsed.description) updates.description = parsed.description;
      if (parsed.requirements) updates.requirements = parsed.requirements;
      if (parsed.experience_min != null) updates.experience_min = parsed.experience_min;
      if (parsed.experience_max != null) updates.experience_max = parsed.experience_max;
      if (parsed.experience_type) updates.experience_type = parsed.experience_type;
      if (parsed.priority) updates.priority = parsed.priority;
      if (parsed.required_languages?.length) updates.required_languages = parsed.required_languages;
      if (parsed.max_notice_days != null) updates.max_notice_days = parsed.max_notice_days;
      if (parsed.education_level) updates.education_level = parsed.education_level;
      if (parsed.english_level_required) updates.english_level_required = parsed.english_level_required;
      if (parsed.pay_type) updates.pay_type = parsed.pay_type;
      if (parsed.perk_ids?.length) updates.perk_ids = parsed.perk_ids;
      if (parsed.required_skills?.length) {
        updates.required_skills = parsed.required_skills.map(
          (s): RequiredSkill => ({
            skill: s.skill,
            must_have: false,
            skill_id: s.skill_id,
          }),
        );
      }

      if (parsed.ctc_min_paise != null || parsed.ctc_max_paise != null) {
        setCtcPeriod("yearly");
        if (parsed.ctc_min_paise != null) updates.ctc_min = Math.round(parsed.ctc_min_paise / 100);
        if (parsed.ctc_max_paise != null) updates.ctc_max = Math.round(parsed.ctc_max_paise / 100);
      }

      form.setValues({ ...form.values, ...updates });
      setParsedFields(keysWithValues({ ...parsed, ...updates }));
      setJdHint("Job fields filled from the JD. Review everything — rate card was left unchanged.");
      notifications.show({
        color: "green",
        title: "JD imported",
        message: "Review all fields — rate card was left unchanged.",
      });
      setActive(DETAILS_STEP);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not parse job description";
      setJdError(message);
      setJdFile(null);
      notifications.show({
        color: "red",
        title: "Could not parse JD",
        message,
      });
    } finally {
      setJdBusy(false);
    }
  }

  const saveMutation = useMutation({
    mutationFn: async (publish: boolean) => {
      if (stages.length === 0) throw new Error("Add at least one hiring stage");
      const payload = buildJobPayload();
      let id = jobId;
      if (isEdit && id) {
        await jobsApi.update(id, payload);
      } else {
        const job = await jobsApi.create(payload);
        id = job.id;
      }
      await hiringFlowsApi.setJobStages(id!, stages);
      if (publish) {
        await jobsApi.publish(id!);
      }
      return id!;
    },
    onSuccess: (id, publish) => {
      notifications.show({
        color: "green",
        title: publish ? "Submitted for review" : "Draft saved",
        message: publish
          ? "Your job is pending AxioHire fee review. Recruiters will see it once approved."
          : "Job saved as draft.",
      });
      queryClient.invalidateQueries({ queryKey: ["hr-jobs"] });
      navigate(`/hr/jobs/${id}`);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
    onSettled: () => {
      saveInFlightRef.current = false;
    },
  });

  function saveJob(publish: boolean) {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    saveMutation.mutate(publish);
  }

  const saveTemplateMutation = useMutation({
    mutationFn: () => {
      const companyId = companyQuery.data!.company_id;
      return hiringFlowsApi.createTemplate(companyId, {
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        stages,
      });
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Template saved", message: "Company template created." });
      queryClient.invalidateQueries({ queryKey: ["hiring-templates"] });
      setSaveTemplateOpen(false);
      setTemplateName("");
      setTemplateDescription("");
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Something went wrong";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  function handleStepClick(step: number) {
    if (step === REVIEW_STEP && stages.length === 0) {
      notifications.show({ color: "red", message: "Add at least one stage before reviewing" });
      return;
    }
    if (step > DETAILS_STEP) {
      const err = form.validate();
      if (err.hasErrors) {
        setActive(DETAILS_STEP);
        return;
      }
    }
    setActive(step);
  }

  const v = form.values;
  const companyVerified = companyQuery.data?.verification_status === "verified";
  const canPublishJob = hrCanPublish(companyQuery.data) && !!contractQuery.data?.length;
  const publishBlockReason = !companyVerified
    ? HR_KYC_COPY.cannotPublishCompany
    : !hrCanPublish(companyQuery.data)
      ? HR_KYC_COPY.cannotPublishKyc
      : !contractQuery.data?.length
        ? HR_KYC_COPY.cannotPublishContract
        : "";
  const showFixed = v.pay_type !== "incentive_only";
  const showIncentive = v.pay_type !== "fixed";
  const nextCtcPeriod = otherCtcPeriod(ctcPeriod);

  if (isEdit && jobQuery.data && !EDITABLE_JOB_STATUSES.has(jobQuery.data.status)) {
    return (
      <Stack gap="lg" maw="var(--axio-unit-800)">
        <PageHeader
          icon={NAV_ICONS.jobs}
          title={NAV_COPY.hr.editJob}
          subtitle="Commercial terms are locked once a job is under review or live."
        />
        <Alert color="yellow" title="Editing locked">
          {LIVE_JOB_EDIT_LOCKED_MESSAGE} Pause it before changing its rate card or other commercial terms.
        </Alert>
        <Button variant="default" onClick={() => navigate(`/hr/jobs/${jobId}`)}>
          Back to job
        </Button>
      </Stack>
    );
  }

  function applyCtcPeriod(next: CtcPeriod) {
    if (next === ctcPeriod) return;
    form.setValues({
      ctc_min: convertCtcForPeriod(form.values.ctc_min, ctcPeriod, next),
      ctc_max: convertCtcForPeriod(form.values.ctc_max, ctcPeriod, next),
    });
    setCtcPeriod(next);
  }
  const skillSuggestions = (SEGMENT_SKILL_SUGGESTIONS[v.segment] ?? SEGMENT_SKILL_SUGGESTIONS.default)
    .filter((s) => !v.required_skills.some((r) => r.skill === s || r.skill.toLowerCase() === s.toLowerCase()))
    .slice(0, 8);

  return (
    <Stack gap={axioTokens.unit[32]} maw="var(--axio-unit-800)">
      <PageHeader
        icon={isEdit ? NAV_ICONS.jobs : NAV_ICONS.postJob}
        title={isEdit ? NAV_COPY.hr.editJob : NAV_COPY.hr.postJob}
        subtitle="Upload a JD — we fill the form. You review, then publish."
      />

      {!companyVerified && (
        <Alert color="yellow">{ORG_KYC_COPY.unverifiedClientBody}</Alert>
      )}
      {companyVerified && !hrCanPublish(companyQuery.data) && (
        <Alert color="yellow">{HR_KYC_COPY.bannerBody}</Alert>
      )}
      {(!contractQuery.data || contractQuery.data.length === 0) && (
        <Alert color="yellow">
          No active company rate cards found. You can save draft jobs, but publishing is blocked until admin creates and
          activates a contract.
        </Alert>
      )}

      <Stepper active={active} onStepClick={handleStepClick}>
        <Stepper.Step label="Import" description="JD or start fresh">
          <Paper withBorder p="lg" mt="md">
            <Stack gap={axioTokens.unit[32]}>
              <ResumeParseSection
                file={jdFile}
                busy={jdBusy}
                error={jdError}
                hint={jdHint}
                eyebrow="Job description"
                title="Parse from JD"
                description="Drop a PDF/DOCX. We fill title, segment, cities, pay, skills, and more. Rate card is never changed."
                busyLabel="Reading your JD…"
                onFileChange={handleJdImport}
              />
              <Button variant="default" onClick={() => setActive(DETAILS_STEP)}>
                Start from scratch
              </Button>
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Job details" description="Role & compensation">
          <Stack gap={JOB_SECTION_GAP} mt="md">
              <JobDetailsSection
                title="Role overview"
                description="Define the position, where candidates will work, and how they can attend."
              >
                <SimpleGrid cols={JOB_DETAILS_COLUMNS} spacing={JOB_FIELD_GAP}>
                  <AiFilledField aiFilled={filled("title")} showGap={showAiGaps}>
                    <TextInput label="Job title" required {...form.getInputProps("title")} />
                  </AiFilledField>
                  <AiFilledField aiFilled={filled("segment")} showGap={showAiGaps}>
                    <Select
                      label="Segment"
                      withAsterisk
                      searchable
                      data={segmentOptions}
                      renderOption={renderSegmentOption}
                      filter={catalogFuzzyFilter}
                      limit={40}
                      nothingFoundMessage="No matching segments"
                      {...form.getInputProps("segment")}
                    />
                  </AiFilledField>
                  <AiFilledField aiFilled={filled("role_family_code")} showGap={showAiGaps}>
                    <Select
                      label={ROLE_FAMILY_LABEL}
                      placeholder={ROLE_FAMILY_PLACEHOLDER}
                      searchable
                      clearable
                      data={roleFamilyOptions}
                      filter={catalogFuzzyFilter}
                      limit={40}
                      nothingFoundMessage="No matching role families"
                      {...form.getInputProps("role_family_code")}
                    />
                  </AiFilledField>
                </SimpleGrid>
                <AiFilledField aiFilled={filled("cities")} showGap={showAiGaps}>
                  <CityMultiSelect
                    label="Hiring cities"
                    description="Up to 5 locations — candidates can match any of these"
                    required
                    value={form.values.cities}
                    onChange={(cities) => form.setFieldValue("cities", cities)}
                    error={form.errors.cities}
                  />
                </AiFilledField>
                <AiFilledField aiFilled={filled("work_mode")} showGap={showAiGaps}>
                  <OptionRadioGroup
                    label="Work location type"
                    options={WORK_MODE_OPTIONS}
                    {...form.getInputProps("work_mode")}
                  />
                </AiFilledField>
                <Stack gap="md">
                  <Switch
                    label="Walk-in interview"
                    description="Candidates can arrive directly without prior scheduling"
                    {...form.getInputProps("is_walkin", { type: "checkbox" })}
                  />
                  {form.values.is_walkin ? (
                    <TextInput
                      label="Walk-in venue address"
                      required
                      placeholder="Full address where candidates should arrive"
                      {...form.getInputProps("walkin_address")}
                    />
                  ) : null}
                </Stack>
                <AiFilledField aiFilled={filled("description")} showGap={showAiGaps}>
                  <Textarea label="Description" autosize minRows={8} maxRows={20} {...form.getInputProps("description")} />
                </AiFilledField>
              </JobDetailsSection>

              <JobDetailsSection
                title="Hiring preferences"
                description="Set the urgency, vacancy count, notice window, and language requirements."
              >
                <SimpleGrid cols={JOB_DETAILS_COLUMNS} spacing={JOB_FIELD_GAP}>
                  <Select
                    label="Priority"
                    data={[
                      { value: "normal", label: "Normal" },
                      { value: "urgent", label: "Urgent — hide notice over 30 days" },
                    ]}
                    {...form.getInputProps("priority")}
                  />
                  <NumberInput label="Openings" min={1} {...form.getInputProps("openings_total")} />
                  <NumberInput
                    label="Max notice (days)"
                    description="Hide candidates with a longer notice. Urgent jobs also cap at 30 days."
                    min={0}
                    max={730}
                    {...form.getInputProps("max_notice_days")}
                  />
                  <TextInput
                    label="Required languages"
                    description="Comma-separated. Candidates missing these are hidden from Explore."
                    value={form.values.required_languages.join(", ")}
                    onChange={(event) =>
                      form.setFieldValue(
                        "required_languages",
                        event.currentTarget.value
                          .split(",")
                          .map((language) => language.trim())
                          .filter(Boolean),
                      )
                    }
                  />
                </SimpleGrid>
                <AiFilledField aiFilled={filled("requirements")} showGap={showAiGaps}>
                  <Textarea label="Requirements" autosize minRows={6} maxRows={18} {...form.getInputProps("requirements")} />
                </AiFilledField>
              </JobDetailsSection>

              <JobDetailsSection
                title="Pay & benefits"
                description="Choose how compensation is presented and add the benefits candidates can expect."
              >
                <Stack gap="md">
                  <AiFilledField aiFilled={filled("pay_type")} showGap={showAiGaps}>
                    <PayTypeRadioCards
                      value={form.values.pay_type}
                      onChange={(next) => form.setFieldValue("pay_type", next as PayType)}
                    />
                  </AiFilledField>
                  {showFixed ? (
                    <SimpleGrid cols={JOB_DETAILS_COLUMNS} spacing={JOB_FIELD_GAP}>
                      <NumberInput
                        label="CTC min"
                        min={0}
                        thousandSeparator=","
                        hideControls
                        rightSectionPointerEvents="all"
                        rightSectionWidth={CTC_PERIOD_UNIT_WIDTH}
                        rightSection={
                          <CtcPeriodUnitAnchor
                            period={ctcPeriod}
                            onSwitch={() => applyCtcPeriod(nextCtcPeriod)}
                          />
                        }
                        {...form.getInputProps("ctc_min")}
                      />
                      <NumberInput
                        label="CTC max"
                        min={0}
                        thousandSeparator=","
                        hideControls
                        rightSectionPointerEvents="all"
                        rightSectionWidth={CTC_PERIOD_UNIT_WIDTH}
                        rightSection={
                          <CtcPeriodUnitAnchor
                            period={ctcPeriod}
                            onSwitch={() => applyCtcPeriod(nextCtcPeriod)}
                          />
                        }
                        {...form.getInputProps("ctc_max")}
                      />
                    </SimpleGrid>
                  ) : null}
                  {showIncentive ? (
                    <SimpleGrid cols={JOB_DETAILS_COLUMNS} spacing={JOB_FIELD_GAP}>
                      <NumberInput label="Incentive min (₹/month)" min={0} thousandSeparator="," {...form.getInputProps("incentive_min")} />
                      <NumberInput label="Incentive max (₹/month)" min={0} thousandSeparator="," {...form.getInputProps("incentive_max")} />
                    </SimpleGrid>
                  ) : null}
                  <Stack gap="xs">
                    <Text size="xs" fw="var(--axio-font-weight-semibold)" c="dimmed">Salary shown to candidates</Text>
                    {showFixed ? (
                      <Text size="sm">Fixed / month: {rupeesLabel(form.values.ctc_min)} – {rupeesLabel(form.values.ctc_max)}</Text>
                    ) : null}
                    {showIncentive ? (
                      <Text size="sm">Incentive / month: {rupeesLabel(form.values.incentive_min)} – {rupeesLabel(form.values.incentive_max)}</Text>
                    ) : null}
                    <Text size="sm" fw="var(--axio-font-weight-medium)">
                      Earning potential: {rupeesLabel((form.values.ctc_min ?? 0) + (form.values.incentive_min ?? 0))} – {rupeesLabel((form.values.ctc_max ?? 0) + (form.values.incentive_max ?? 0))} / month
                    </Text>
                  </Stack>
                </Stack>
                <Stack gap="xs">
                  <Text size="sm" fw="var(--axio-font-weight-medium)">
                    Perks & benefits <Text span size="xs" c="dimmed">(Optional)</Text>
                  </Text>
                  <Group gap="xs" wrap="wrap">
                    {form.values.perk_ids.map((perkId) => (
                      <Badge
                        key={perkId}
                        variant="filled"
                        rightSection={
                          <ActionIcon
                            size="xs"
                            variant="transparent"
                            onClick={() => form.setFieldValue("perk_ids", form.values.perk_ids.filter((id) => id !== perkId))}
                          >
                            <IconX size={axioTokens.unit[10]} />
                          </ActionIcon>
                        }
                      >
                        {perkLabels([perkId])[0]}
                      </Badge>
                    ))}
                  </Group>
                  {/*<Text size="xs" c="dimmed">Suggestions — click to add:</Text>*/}
                  <Group gap="xs" wrap="wrap">
                    {PERK_CATALOG.filter((perk) => !form.values.perk_ids.includes(perk.id)).map((perk) => (
                      <Button
                        key={perk.id}
                        size="xs"
                        variant="default"
                        leftSection={<IconPlus size={axioTokens.unit[10]} />}
                        onClick={() => form.setFieldValue("perk_ids", [...form.values.perk_ids, perk.id])}
                      >
                        {perk.label}
                      </Button>
                    ))}
                  </Group>
                </Stack>
              </JobDetailsSection>

              <JobDetailsSection
                title="Candidate requirements"
                description="Describe the experience, education, language level, and skills needed for this role."
              >
                <OptionRadioGroup
                  label="Experience required"
                  options={EXPERIENCE_TYPE_OPTIONS}
                  {...form.getInputProps("experience_type")}
                />
                {form.values.experience_type !== "fresher" ? (
                  <SimpleGrid cols={JOB_DETAILS_COLUMNS} spacing={JOB_FIELD_GAP}>
                    <NumberInput
                      label="Experience min (years)"
                      min={0}
                      max={50}
                      decimalScale={1}
                      step={0.1}
                      {...form.getInputProps("experience_min")}
                    />
                    <NumberInput
                      label="Experience max (years)"
                      min={0}
                      max={50}
                      decimalScale={1}
                      step={0.1}
                      {...form.getInputProps("experience_max")}
                    />
                  </SimpleGrid>
                ) : null}
                <EducationLevelPills
                  variant="job"
                  label="Minimum education required"
                  value={form.values.education_level}
                  onChange={(next) => form.setFieldValue("education_level", next)}
                />
                <OptionRadioGroup
                  label="English level required"
                  description="Optional"
                  options={ENGLISH_LEVEL_REQUIRED_OPTIONS}
                  value={form.values.english_level_required ?? ""}
                  onChange={(next) => form.setFieldValue("english_level_required", (next as EnglishLevelRequired) || null)}
                />
                <RequiredSkillsEditor
                  value={form.values.required_skills}
                  onChange={(required_skills) => form.setFieldValue("required_skills", required_skills)}
                />
                {skillSuggestions.length > 0 ? (
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed">Suggested skills for this segment:</Text>
                    <Group gap="xs" wrap="wrap">
                      {skillSuggestions.map((skill) => (
                        <Button
                          key={skill}
                          size="xs"
                          variant="default"
                          leftSection={<IconPlus size={axioTokens.unit[10]} />}
                          onClick={() =>
                            form.setFieldValue("required_skills", [
                              ...form.values.required_skills,
                              { skill, must_have: false },
                            ])
                          }
                        >
                          {skill}
                        </Button>
                      ))}
                    </Group>
                  </Stack>
                ) : null}
              </JobDetailsSection>

              <JobDetailsSection
                title="Commercial terms"
                description="Select the company rate card that governs this job and disclose candidate fees when applicable."
              >
                <Select
                  label="Rate card"
                  description="Commercial terms for this job (from your company contract)"
                  required
                  searchable
                  data={(contractQuery.data ?? []).map((contract) => ({
                    value: contract.id,
                    label: `${contract.name}${contract.is_default ? " (default)" : ""} — ${
                      contract.fee_basis === "flat"
                        ? formatPaise(contract.fee_flat_paise ?? 0)
                        : `${contract.fee_pct_ctc ?? "—"}% CTC`
                    }`,
                  }))}
                  value={form.values.company_contract_id || null}
                  onChange={applyContractSelection}
                  error={form.errors.company_contract_id}
                  nothingFoundMessage="No active rate cards"
                />
                <Stack gap="xs">
                  <Text fw="var(--axio-font-weight-semibold)">Contract terms (read-only)</Text>
                  {selectedContract ? (
                    <>
                      <Text size="sm">Rate card: {selectedContract.name}</Text>
                      <Text size="sm">
                        Fee basis: {selectedContract.fee_basis === "flat" ? "Flat fee per hire" : "% of CTC"}
                      </Text>
                      <Text size="sm">
                        {selectedContract.fee_basis === "flat"
                          ? `Fee per hire: ${formatPaise(selectedContract.fee_flat_paise ?? 0)}`
                          : `Fee (% of CTC): ${selectedContract.fee_pct_ctc ?? "—"}%`}
                      </Text>
                      <Text size="sm">Guarantee window: {selectedContract.guarantee_days} days</Text>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Select a rate card above. Admin must create and activate one if none appear.
                    </Text>
                  )}
                </Stack>
                <Switch
                  label="Requires joining fee or deposit from candidate"
                  description="Disclosed to candidates for transparency and trust"
                  color="red"
                  {...form.getInputProps("requires_joining_fee", { type: "checkbox" })}
                />
              </JobDetailsSection>
          </Stack>
        </Stepper.Step>

        <Stepper.Step label="Hiring flow" description="Stages & communication">
          <Paper withBorder p="lg" mt="md">
            <Stack gap={axioTokens.unit[32]}>
              <FlowTemplatePicker
                companyId={companyQuery.data?.company_id}
                selectedId={selectedId}
                selectedSource={selectedSource}
                hasEdits={hasEdits}
                onSelect={handleFlowSelect}
              />
              <Divider />
              <div>
                <Text size="sm" fw="var(--axio-font-weight-semibold)" mb="xs">
                  Preview
                </Text>
                {stages.length > 0 ? (
                  <FlowPreviewStepper stages={stages} />
                ) : (
                  <Text size="sm" c="dimmed">
                    No stages yet — pick a template or add stages below.
                  </Text>
                )}
              </div>
              <Divider />
              <HiringFlowBuilder stages={stages} onChange={handleStagesChange} />
              {companyQuery.data?.company_id && stages.length > 0 && (
                <Button variant="default" onClick={() => setSaveTemplateOpen(true)}>
                  Save as company template
                </Button>
              )}
              <Divider />
              <Stack gap="md">
                <Text fw="var(--axio-font-weight-semibold)">Candidate contact preference</Text>
                <Text size="sm" c="dimmed">Do you want candidates to contact you after applying?</Text>
                <Radio.Group {...form.getInputProps("contact_preference")}>
                  <Stack gap="sm">
                    <Radio value="candidates_call_recruiter" label="Yes — candidates can call / WhatsApp me directly" />
                    <Radio value="candidates_call_other" label="Yes — forward calls to another team member" />
                    <Radio value="recruiter_contacts_first" label="No — I will contact shortlisted candidates" />
                  </Stack>
                </Radio.Group>
              </Stack>
              <Stack gap="md">
                <Text fw="var(--axio-font-weight-semibold)">WhatsApp application alerts</Text>
                <Radio.Group {...form.getInputProps("whatsapp_alert")}>
                  <Stack gap="sm">
                    <Radio value="daily_digest" label="Send one daily summary of all applications" />
                    <Radio value="per_application_self" label="Alert me for every application — on my number" />
                    <Radio value="per_application_other" label="Alert another recruiter for every application" />
                  </Stack>
                </Radio.Group>
              </Stack>
            </Stack>
          </Paper>
        </Stepper.Step>

        <Stepper.Step label="Review" description="Confirm & publish">
          <Paper withBorder p="lg" mt="md">
            <Stack gap="sm">
              <Text fw="var(--axio-font-weight-semibold)">{v.title}</Text>
              <Text size="sm" c="dimmed">
                {SEGMENT_LABELS[v.segment]} · {formatCities(v.cities)}
                {" · "}{WORK_MODE_LABELS[v.work_mode]}
                {v.is_walkin ? " · Walk-in" : ""}
                {" · "}{v.openings_total} opening(s)
              </Text>
              {v.is_walkin && v.walkin_address && (
                <Text size="sm">Walk-in venue: {v.walkin_address}</Text>
              )}
              <Text size="sm">Pay type: {PAY_TYPE_LABELS[v.pay_type]}</Text>
              <Text size="sm">
                CTC:{" "}
                {v.ctc_min != null || v.ctc_max != null
                  ? `${v.ctc_min != null ? `₹${v.ctc_min.toLocaleString("en-IN")}` : "—"} – ${
                      v.ctc_max != null ? `₹${v.ctc_max.toLocaleString("en-IN")}` : "—"
                    }${ctcPeriod === "monthly" ? "/month" : "/year"}`
                  : "—"}
              </Text>
              {showIncentive && (
                <Text size="sm">
                  Incentive / month: {rupeesLabel(v.incentive_min)} – {rupeesLabel(v.incentive_max)}
                </Text>
              )}
              {v.perk_ids.length > 0 && (
                <Text size="sm">Perks: {perkLabels(v.perk_ids).join(", ")}</Text>
              )}
              <Text size="sm">Experience: {EXPERIENCE_TYPE_LABELS[v.experience_type]}</Text>
              {v.education_level && (
                <Text size="sm">Education: {educationLevelLabel(v.education_level)}</Text>
              )}
              {v.english_level_required && (
                <Text size="sm">English required: {v.english_level_required}</Text>
              )}
              {v.requires_joining_fee && (
                <Text size="sm" c="red">Joining fee or deposit required</Text>
              )}
              <Text size="sm">Contact: {CONTACT_PREFERENCE_LABELS[v.contact_preference]}</Text>
              <Text size="sm">WhatsApp alerts: {WHATSAPP_ALERT_LABELS[v.whatsapp_alert]}</Text>
              <Text size="sm" fw="var(--axio-font-weight-semibold)" mt="sm">
                Skills ({v.required_skills.length})
              </Text>
              <Text size="sm" c="dimmed">
                {v.required_skills.length === 0
                  ? "None set"
                  : v.required_skills
                      .map((s) => `${s.skill}${s.must_have ? " (must)" : ""}`)
                      .join(", ")}
              </Text>
              <Text size="sm" fw="var(--axio-font-weight-semibold)" mt="md">
                Hiring flow ({stages.length} stages)
              </Text>
              {stages.map((s, i) => (
                <Text key={i} size="sm">
                  {i + 1}. {s.name} ({STAGE_TYPE_LABELS[s.stage_type]})
                </Text>
              ))}
            </Stack>
          </Paper>
        </Stepper.Step>
      </Stepper>

      <Group justify="space-between">
        <Button variant="default" disabled={active === IMPORT_STEP} onClick={() => setActive((a) => a - 1)}>
          Back
        </Button>
        <Group>
          {active < REVIEW_STEP ? (
            <Button
              color="violet"
              onClick={() => {
                if (active === DETAILS_STEP) {
                  const err = form.validate();
                  if (err.hasErrors) return;
                }
                if (active === FLOW_STEP && stages.length === 0) {
                  notifications.show({ color: "red", message: "Add at least one stage" });
                  return;
                }
                setActive((a) => a + 1);
              }}
            >
              Next
            </Button>
          ) : (
            <>
              <Button variant="default" loading={saveMutation.isPending} onClick={() => saveJob(false)}>
                Save draft
              </Button>
              <Tooltip label={publishBlockReason} disabled={canPublishJob}>
                <Button
                  color="violet"
                  loading={saveMutation.isPending}
                  disabled={!canPublishJob}
                  onClick={() => saveJob(true)}
                  data-testid="job-publish"
                >
                  Publish job
                </Button>
              </Tooltip>
            </>
          )}
        </Group>
      </Group>

      <Modal
        opened={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        title="Save as company template"
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Save the current {stages.length} stage{stages.length !== 1 ? "s" : ""} as a reusable template for your company.
          </Text>
          <TextInput
            label="Template name"
            required
            value={templateName}
            onChange={(e) => setTemplateName(e.currentTarget.value)}
          />
          <Textarea
            label="Description (optional)"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.currentTarget.value)}
            minRows={2}
          />
          <Button
            color="violet"
            loading={saveTemplateMutation.isPending}
            disabled={templateName.trim().length < 2}
            onClick={() => saveTemplateMutation.mutate()}
          >
            Save template
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
